import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Button,
  Box,
  styled,
  alpha,
  CircularProgress,
} from '@mui/material'
import { FiCheckCircle, FiSettings, FiSave } from 'react-icons/fi'
import { useAuthContext } from '../../contexts/AuthContext'
import { useConsent } from '../../contexts/ConsentContext'
import { updateUserSettings } from '../../services/userService'

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
}))

const SuccessBox = styled(Box)(() => ({
  padding: 20,
  textAlign: 'center',
  color: '#059669',
  fontWeight: 600,
}))

const SettingsModal = ({ isOpen, onClose, onSave }) => {
  const { user, token } = useAuthContext()
  const { openPreferences } = useConsent()

  const handleOpenCookiePreferences = () => {
    onClose()
    openPreferences()
  }
  const [department, setDepartment] = useState('')
  const [preferredGreeting, setPreferredGreeting] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  // Load initial values
  useEffect(() => {
    if (user && isOpen) {
      setDepartment(user.department || '')
      setPreferredGreeting(user.preferred_greeting || '')
      setError('')
      setSuccess(false)
    }
  }, [user, isOpen])

  const handleSave = async () => {
    setIsSaving(true)
    setError('')

    try {
      await updateUserSettings(token, {
        department,
        preferred_greeting: preferredGreeting,
      })

      setSuccess(true)
      await onSave()

      setTimeout(() => {
        onClose()
      }, 1500)
    } catch (e) {
      setError(e.response?.data?.detail || 'Impossibile salvare le impostazioni.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={isSaving ? () => {} : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
          bgcolor: theme =>
            theme.palette.mode === 'dark'
              ? alpha('#fff', 0.06)
              : theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <IconWrapper>
          <FiSettings size={48} />
        </IconWrapper>

        <Typography variant="h5" fontWeight={600}>
          Impostazioni Profilo
        </Typography>

        <Typography variant="body2" color="text.secondary" mt={1}>
          Personalizza la tua esperienza con F.R.A.N.C.A.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <SuccessBox>
            <FiCheckCircle size={32} />
            <Typography mt={1}>Impostazioni salvate!</Typography>
          </SuccessBox>
        ) : (
          <Box mt={1} display="flex" flexDirection="column" gap={3}>
            {/* Dipartimento */}
            <TextField
              label="Dipartimento"
              value={department}
              onChange={e => setDepartment(e.target.value)}
              placeholder="Es. IT, Marketing"
              disabled={isSaving}
              fullWidth
            />

            {/* Team */}
            <Box>
              <TextField
                label="Team Assegnato"
                value={user?.team || 'Nessun team assegnato'}
                fullWidth
                disabled
              />
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
              >
                Il team viene assegnato dagli amministratori
              </Typography>
            </Box>

            {/* Saluto Preferito */}
            <TextField
              label="Saluto Preferito"
              value={preferredGreeting}
              onChange={e => setPreferredGreeting(e.target.value)}
              placeholder="Come F.R.A.N.C.A. si rivolge a te"
              disabled={isSaving}
              fullWidth
            />

            {/* Cookie preferences */}
            <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Privacy e cookie
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={handleOpenCookiePreferences}
                sx={{ textTransform: 'none', p: 0, minHeight: 0 }}
              >
                Gestisci preferenze cookie
              </Button>
            </Box>

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ justifyContent: 'center', gap: 1, p: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={isSaving}
          >
            Annulla
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={
              isSaving ? <CircularProgress size={16} thickness={5} /> : <FiSave />
            }
          >
            {isSaving ? 'Salvando...' : 'Salva'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default SettingsModal
